/**
 * 3e: how one statement line's difference got to the number on the screen.
 *
 * A re-uploaded statement that states a different amount for a policy already
 * on file now CORRECTS that line instead of adding a second one. That is what
 * the owner asked for, and on its own it would quietly rewrite money: the
 * difference a broker disputed with the insurer last week would simply be a
 * different number today, with nothing saying it had ever been anything else.
 * This drawer is the answer to "since when, and on whose upload?".
 *
 * Every row is a `StatementLineRevision`: the amount before and after, the
 * difference each of those produced, who uploaded the statement that changed
 * it, and the NET amount that moved on the ledger for the correction (the
 * platform never reverses the old adjustment and re-posts the whole new one).
 *
 * The three states are kept apart on purpose. A failed request rendering as
 * "this line was never corrected" is the same lie this tab has shipped before:
 * an authoritative "there is none" over a request that never answered.
 */

import { Badge, Button } from '@/components/common';
import { ReportSideDrawer } from '@/pages/reports/ReportSideDrawer';
import { useStatementLineRevisions } from '@/hooks/useReconciliation';
import { getErrorMessage } from '@/services';
import type { StatementLine, StatementLineRevision } from '@/services/reconciliation';
import { formatCurrency, formatDateTime } from '@/utils';
import {
  TONE_CLASS,
  differenceFromDelta,
  differenceTone,
  formatSignedAmount,
  lineDifference,
  toAmount,
} from './commissionComparison';

/** "3 Feb 2026, 4:05 pm" — a correction is an event, so it keeps its time. */
function when(value: string) {
  return formatDateTime(value);
}

function DifferenceText({ difference }: { difference: number | null }) {
  return (
    <span className={`font-medium ${TONE_CLASS[differenceTone(difference)]}`}>
      {formatSignedAmount(difference)}
    </span>
  );
}

function RevisionRow({ revision }: { revision: StatementLineRevision }) {
  const before = differenceFromDelta(revision.previous_delta);
  const after = differenceFromDelta(revision.new_delta);
  // `ledger_change` is already signed the way the running balance moved, so it
  // is read straight -- unlike a `delta`, which the tab shows the other way up.
  const ledgerChange = toAmount(revision.ledger_change);

  return (
    <li className="border-b border-gray-100 py-4 last:border-b-0 dark:border-gray-700">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {formatCurrency(revision.previous_stated_amount)}
          <span className="mx-2 text-gray-400 dark:text-gray-500" aria-label="changed to">
            →
          </span>
          {formatCurrency(revision.new_stated_amount)}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{when(revision.created_at)}</p>
      </div>
      <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div className="flex justify-between gap-2">
          <dt className="text-gray-500 dark:text-gray-400">Difference</dt>
          <dd>
            <DifferenceText difference={before} />
            <span className="mx-1.5 text-gray-400 dark:text-gray-500">→</span>
            <DifferenceText difference={after} />
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-gray-500 dark:text-gray-400">Posted to ledger</dt>
          <dd>
            <DifferenceText difference={ledgerChange} />
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-gray-500 dark:text-gray-400">Uploaded by</dt>
          <dd className="text-gray-700 dark:text-gray-300">
            {revision.uploaded_by_name || 'Not recorded'}
          </dd>
        </div>
        {revision.source_file && (
          <div className="flex justify-between gap-2">
            <dt className="text-gray-500 dark:text-gray-400">From file</dt>
            <dd className="truncate text-gray-700 dark:text-gray-300" title={revision.source_file}>
              {revision.source_file}
            </dd>
          </div>
        )}
      </dl>
    </li>
  );
}

export function StatementLineHistoryDrawer({
  line,
  isOpen,
  onClose,
}: {
  line: StatementLine | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const query = useStatementLineRevisions(isOpen && line ? line.id : null);
  const revisions = query.data?.results ?? [];

  if (!line) return null;

  const current = lineDifference(line);

  return (
    <ReportSideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={`History — ${line.policy_number}`}
      subtitle="Every correction a later statement made to this line"
    >
      {/* Where the line stands now, so the history below has something to end
          at. Rendered from the row the tab already has: it never waits on the
          request. */}
      <dl className="mb-4 grid grid-cols-3 gap-4 rounded-lg bg-gray-50 p-4 text-sm dark:bg-gray-900/40">
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">Received</dt>
          <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-100">
            {formatCurrency(line.stated_amount)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">Calculated</dt>
          <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-100">
            {line.expected_amount != null ? (
              formatCurrency(line.expected_amount)
            ) : (
              <span className="text-gray-400 dark:text-gray-500">—</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">Difference</dt>
          <dd className="mt-0.5">
            <DifferenceText difference={current} />
          </dd>
        </div>
      </dl>

      {query.isLoading ? (
        <div className="animate-pulse space-y-3" aria-label="Loading history">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded bg-gray-100 dark:bg-gray-700" />
          ))}
        </div>
      ) : query.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm dark:border-red-900 dark:bg-red-950/30">
          <p className="font-medium text-red-800 dark:text-red-300">
            Could not load this line's history
          </p>
          <p className="mt-1 text-red-700 dark:text-red-400">{getErrorMessage(query.error)}</p>
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
            This is not the same as the line never having been corrected.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => query.refetch()}>
            Try again
          </Button>
        </div>
      ) : revisions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-gray-600">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            This line has never been corrected
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            The amount above is the one the insurer first stated. Re-uploading a statement with a
            different amount for this policy updates the line and records it here.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {revisions.length} correction{revisions.length === 1 ? '' : 's'}
            </h4>
            <Badge variant="default" size="sm">
              Newest first
            </Badge>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {revisions.map((revision) => (
              <RevisionRow key={revision.id} revision={revision} />
            ))}
          </ul>
        </>
      )}
    </ReportSideDrawer>
  );
}
